import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { PlanId } from '../lib/stripe'

export type UseSubscriptionResult = {
  loading: boolean
  error: string | null
  planId?: PlanId
  status?: string
  currentPeriodEnd?: string
  isActive: boolean
  customProcessorsEnabled: boolean
  refresh: () => Promise<void>
}

export function useSubscription(
  id: string | null | undefined,
  type: 'merchant' | 'provider' = 'merchant'
): UseSubscriptionResult {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [planId, setPlanId] = useState<PlanId | undefined>(undefined)
  const [status, setStatus] = useState<string | undefined>(undefined)
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<string | undefined>(undefined)
  const [customProcessorsEnabled, setCustomProcessorsEnabled] = useState<boolean>(false)

  const fetchSub = useCallback(async () => {
    if (!id) {
      setPlanId(undefined)
      setStatus(undefined)
      setCurrentPeriodEnd(undefined)
      setCustomProcessorsEnabled(false)
      setError(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      // Determine which column to filter by
      const filterColumn = type === 'provider' ? 'provider_id' : 'merchant_id'

      // Prefer unified subscriptions table
      const { data: sub, error: subErr } = await supabase
        .from('subscriptions')
        .select('plan_id, status, current_period_end, custom_processors_enabled')
        .eq(filterColumn, id)
        .maybeSingle()

      if (!subErr && sub) {
        const p = (sub as any).plan_id as PlanId | undefined
        const s = (sub as any).status as string | undefined
        const end = (sub as any).current_period_end as string | undefined
        const cpe = Boolean((sub as any).custom_processors_enabled)
        setPlanId(p)
        setStatus(s)
        setCurrentPeriodEnd(end)
        // Derive active plan and fallback CPE if needed
        const now = new Date()
        const expOk = !end || new Date(end) > now
        const derived = s === 'active' && expOk && (p === 'premium' || p === 'enterprise')
        setCustomProcessorsEnabled(cpe || derived)
        return
      }

      // Fallback to legacy subscription in config_object (only for merchants)
      if (type === 'merchant') {
        const { data: cfg } = await supabase
          .from('merchant_config')
          .select('config_object')
          .eq('merchant_id', id)
          .single()
        const legacy: any = (cfg as any)?.config_object?.subscription
        const p = legacy?.planId as PlanId | undefined
        const s = legacy?.status as string | undefined
        const end = legacy?.currentPeriodEnd || legacy?.expiresAt
        setPlanId(p)
        setStatus(s)
        setCurrentPeriodEnd(end)
        const now = new Date()
        const expOk = !end || new Date(end) > now
        const derived = s === 'active' && expOk && (p === 'premium' || p === 'enterprise')
        setCustomProcessorsEnabled(derived)
      } else {
        // No subscription found for provider
        setPlanId(undefined)
        setStatus(undefined)
        setCurrentPeriodEnd(undefined)
        setCustomProcessorsEnabled(false)
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load subscription')
      setPlanId(undefined)
      setStatus(undefined)
      setCurrentPeriodEnd(undefined)
      setCustomProcessorsEnabled(false)
    } finally {
      setLoading(false)
    }
  }, [id, type])

  useEffect(() => {
    fetchSub()
  }, [fetchSub])

  const isActive = (() => {
    if (!status) return false
    const now = new Date()
    const expOk = !currentPeriodEnd || new Date(currentPeriodEnd) > now
    // A subscription is active if it's either 'active' or 'trialing' with valid expiration
    return (status === 'active' && expOk) || (status === 'trialing' && expOk)
  })()

  return {
    loading,
    error,
    planId,
    status,
    currentPeriodEnd,
    isActive,
    customProcessorsEnabled,
    refresh: fetchSub,
  }
}

